<template>
  <div class="post-search-page d-flex flex-column h-100">
    <div class="d-flex justify-content-between align-items-center w-100 py-2 px-2">
      <h1 class="mb-0 text-nowrap">{{ $t('postSearch.inbox') }}</h1>
      <div class="d-flex justify-content-end">
        <feather-icon
          v-b-tooltip="{ title: $t('common.refresh'), placement: 'bottom', trigger: 'hover' }"
          icon="RefreshCwIcon"
          size="22"
          class="cursor-pointer text-body"
          :class="{ 'fa-spin': refreshing }"
          @click="refreshing= true; getPost(false)"
        />
      </div>
    </div>

    <div
      v-if="!loading && isRestricted && !isOnboarding"
      class="bg-danger text-white mx-2 mb-2 p-2 rounded d-flex align-items-center flex-shrink-0"
    >
      <feather-icon
        icon="AlertTriangleIcon"
        size="38"
        class="mr-2 flex-shrink-0"
      />
      <div>
        <h5 class="text-white font-weight-bolder mb-25">
          {{ $t('postSearch.restriction.title') }}
        </h5>
        <p class="mb-0 small" style="line-height: 1.4;">
          {{ $t('postSearch.restriction.message') }}
        </p>
      </div>
    </div>
    <vue-perfect-scrollbar
      v-if="(!loading && post && post.length > 0) || isOnboarding"
      class="flex-grow-1"
      :settings="perfectScrollbarSettings"
      style="min-height: 0;"
    >
      <template v-if="!isOnboarding">
        <post-list-item
          v-for="item in post"
          :key="item._id"
          :item="item"
          :replies="postReplies[item._id]"
          :is-contact-blocked="item.isBlocked"
          :class="{ 'active-post': selectedPostId === item._id }"
          @click.native="selectedPostId = item._id"
        />
      </template>

      <template v-else>
        <post-list-item
          v-for="dummy in dummyPosts"
          :id="'tour-item-' + dummy.mockStatus"
          :key="dummy._id"
          :item="dummy"
          :replies="dummy.mockReplies"
          :is-contact-blocked="dummy.isBlocked"
          :class="{ 'active-post': selectedPostId === dummy._id }"
          @click.native="selectedPostId = dummy._id"
        />
      </template>
    </vue-perfect-scrollbar>

    <div v-if="loading" class="flex-grow-1 d-flex justify-content-center align-items-center">
      <b-spinner variant="primary" />
    </div>

    <div v-if="(totalRows > filters.limit) || (!loading && totalRows === 0)" class="flex-shrink-0 border-top py-1">
      <b-row class="p-1 justify-content-center">
        <b-col v-if="totalRows > filters.limit && showFilters" cols="auto" class="m-1">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="filters.limit"
            :disabled="loading"
            class="my-0"
          />
        </b-col>
        <p v-if="!loading && totalRows === 0" class="col-12 text-center my-1 text-muted">{{ $t('postSearch.noPosts') }}</p>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import PostService from '@/services/PostService';
import DigitalRepliesService from '@/services/DigitalRepliesService';
import ContactsService from '@/services/ContactService';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import { BSpinner } from 'bootstrap-vue';
import PostListItem from '@/components/PostListItem.vue';

export default {
  name: 'PostSearch',
  components: {
    VuePerfectScrollbar,
    BSpinner,
    PostListItem,
  },
  props: {
    showFilters: {
      required: false,
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: true,
      refreshing: false,
      selectedPostId: null,
      post: [],
      postReplies: {},
      currentPage: 1,
      totalRows: 0,
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
      pollingInterval: null,
      // Mocking posts for onboarding
      dummyPosts: [
        {
          _id: 'mock-1',
          mockStatus: 'unread',
          visitor: { firstname: 'Joe', lastname: 'Bloggs' },
          message: this.$t('onboarding.onboardingExampleUnread'),
          created_at: new Date().toISOString(),
          read_on: null,
          isBlocked: false,
          mockReplies: [],
        },
        {
          _id: 'mock-2',
          mockStatus: 'replied',
          visitor: { firstname: 'Joe', lastname: 'Bloggs' },
          message: this.$t('onboarding.onboardingExampleReplied'),
          created_at: new Date(Date.now() - 274560000).toISOString(),
          read_on: new Date(Date.now() - 274000000).toISOString(),
          isBlocked: false,
          mockReplies: [
            { _id: 'reply-1', status: 'Approved', read_on: null },
          ],
        },
        {
          _id: 'mock-3',
          mockStatus: 'replyRead',
          visitor: { firstname: 'Joe', lastname: 'Bloggs' },
          message: this.$t('onboarding.onboardingExampleRead'),
          created_at: new Date(Date.now() - 447360000).toISOString(),
          read_on: new Date(Date.now() - 447000000).toISOString(),
          isBlocked: false,
          mockReplies: [
            { _id: 'reply-2', status: 'Approved', read_on: new Date().toISOString() },
          ],
        },
        {
          _id: 'mock-4',
          mockStatus: 'replyRejected',
          visitor: { firstname: 'Joe', lastname: 'Bloggs' },
          message: this.$t('onboarding.onboardingExampleReplyRejected'),
          created_at: new Date(Date.now() - 465360000).toISOString(),
          read_on: new Date(Date.now() - 465000000).toISOString(),
          isBlocked: false,
          mockReplies: [
            { _id: 'reply-3', status: 'Rejected', read_on: null },
          ],
        },
        {
          _id: 'mock-5',
          mockStatus: 'read',
          visitor: { firstname: 'Joe', lastname: 'Bloggs' },
          message: this.$t('onboarding.onboardingExampleYouRead'),
          created_at: new Date(Date.now() - 526560000).toISOString(),
          read_on: new Date(Date.now() - 526000000).toISOString(),
          isBlocked: false,
          mockReplies: [],
        },
        {
          _id: 'mock-6',
          mockStatus: 'blocked',
          visitor: { firstname: 'Mary', lastname: 'Bloggs' },
          message: this.$t('onboarding.onboardingExampleBlocked'),
          created_at: new Date(Date.now() - 545040000).toISOString(),
          read_on: null,
          isBlocked: true,
          mockReplies: [],
        },
      ],
    };
  },
  computed: {
    ...mapState({
      filters: state => state.post.filters,
      isOnboarding: state => state.app.isOnboarding,
      isRestricted: state => state.restriction.isRestricted,
    }),
  },
  watch: {
    currentPage() {
      this.getPost();
    },
  },
  mounted() {
    this.updateFilter('sort', '-reviewed_on');
    this.$store.dispatch('restriction/fetchRestrictionStatus');
    this.getPost();
    this.$root.$on('refresh-post-list', this.silentRefresh);

    // Poll for posts every minute silently so the area does not show it is being refreshed
    this.pollingInterval = setInterval(() => {
      this.silentRefresh();
    }, 60000);
  },
  destroyed() {
    this.$root.$off('refresh-post-list', this.silentRefresh)
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
  methods: {
    getPost(isSilent = false) {
      if (isSilent !== true) {
        this.loading = true;
      }
      this.updateFilter('skip', (this.currentPage - 1) * this.filters.limit);

      // Get all the information in a promise to ensure that everything is in place for when loading is set to false
      Promise.all([
        PostService.getAll(this.filters),
        DigitalRepliesService.getAll().catch(err => {
          console.error('Error fetching all digital replies:', err);
          return { data: { data: [] } };
        }),
        ContactsService.getAll({ limit: 1000 }).catch(err => {
          console.error('Error fetching contacts:', err);
          return { data: { data: [] } };
        }),
      ]).then(([postRes, repliesRes, contactsRes]) => {
        this.$store.dispatch('restriction/fetchRestrictionStatus');

        let fetchedPosts = postRes.data.data;
        const fetchedTotalRows = postRes.data.count;

        const allReplies = repliesRes.data?.data || repliesRes.data || [];
        const newPostReplies = {};
        fetchedPosts.forEach(post => {
          // eslint-disable-next-line no-underscore-dangle
          newPostReplies[post._id] = [];
        });

        allReplies.forEach(reply => {
          const relatedPostId = reply.post_id || reply.postId;
          if (relatedPostId) {
            if (newPostReplies[relatedPostId]) {
              newPostReplies[relatedPostId].push(reply);
            } else {
              newPostReplies[relatedPostId] = [reply];
            }
          }
        });

        // Handle Blocked Contacts
        const allContacts = contactsRes.data?.data || contactsRes.data || [];

        const blockedUserIds = new Set(
          allContacts
            .filter(contact => contact.isBlocked || contact.is_blocked)
            .map(contact => contact.id || contact.user_id),
        );

        fetchedPosts = fetchedPosts.map(post => {
          // eslint-disable-next-line no-underscore-dangle
          const userId = post.user_id || (post.visitor && post.visitor._id);

          return {
            ...post,
            isBlocked: blockedUserIds.has(userId),
          };
        });

        this.postReplies = newPostReplies;
        this.post = fetchedPosts;
        this.totalRows = fetchedTotalRows;
        this.loading = false;
        this.refreshing = false;
      }).catch(err => {
        this.loading = false;
        this.refreshing = false;
        console.error('Error fetching inbox data', err);
        this.$toast.error(this.$t('postSearch.error.fetch'), {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      });
    },
    updateFilter(filterName, e) {
      this.$store.commit('post/SET_FILTER_ITEM', { name: filterName, value: e });
    },
    silentRefresh() {
      this.getPost(true);
    },
  },
};
</script>

<style lang="scss">
.active-post {
  background-color: #f0f2f5 !important;

  border-left: 4px solid #a72bbc !important;
  transition: background-color 0.2s ease-in-out;
}

/* Dark mode overrides */
.dark-layout .active-post {
  background-color: #283046 !important;
  border-left-color: #a72bbc !important;
}
</style>
